import { useState } from "react";
import { Tooltip } from "reactstrap";
import "./TariffInfoIcon.css";

let tariffInfoSeq = 0;

const formatPercent = (value) => `${Number(value) || 0}%`;

const parseAmount = (amountText) => {
  const value = Number(String(amountText ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(value) ? value : 0;
};

const vendorNoTariffLine = (info) =>
  info.vendor_checked
    ? `${info.vendor} shows no tariff for part ${info.sku}`
    : `${info.vendor} tariff could not be checked for part ${info.sku}`;

/** Plain-English bullet points explaining how a Tariff Charge cell was calculated. */
export const tariffInfoLines = ({ info, amountText, quoteStatus }) => {
  if (info && typeof info === "object") {
    const system = formatPercent(info.system_percentage);

    if (info.status === "system") {
      return [
        `Standard tariff applied: ${system} (System Settings)`,
        `DigiKey marks this part as tariff-applicable${
          info.digikey_tariff ? ` (${info.digikey_tariff})` : ""
        }`,
        info.vendor_source
          ? `${vendorNoTariffLine(info)}, so the standard rate was used`
          : `Vendor: ${info.vendor} (no vendor-specific tariff)`,
        `Tariff amount = quote price × ${system}`,
      ];
    }

    if (info.status === "none") {
      return [
        "No tariff charged",
        info.digikey_tariff
          ? `DigiKey: ${info.digikey_tariff}`
          : "DigiKey has no tariff information for this part",
        ...(info.vendor_source ? [vendorNoTariffLine(info)] : []),
      ];
    }

    const vendorPercent = Number(info.vendor_percentage) || 0;
    const applied = formatPercent(info.percentage);
    return [
      `${info.vendor} tariff applied: ${applied}`,
      `${info.vendor} tariff for part ${info.sku}: ${formatPercent(vendorPercent)}`,
      vendorPercent > (Number(info.system_percentage) || 0)
        ? `Above the ${system} system limit, so ${system} was used`
        : `Within the ${system} system limit, so ${info.vendor}'s ${formatPercent(vendorPercent)} was used`,
      `Tariff amount = quote price × ${applied}`,
    ];
  }

  if (quoteStatus === "insufficientQuantity") {
    return ["No tariff charged", "Vendor does not have enough stock for this quantity"];
  }
  if (/^\s*N\/A\s*$/i.test(String(amountText ?? "")) || quoteStatus === "unavailable") {
    return ["No tariff charged", "No vendor quote available for this line"];
  }
  if (parseAmount(amountText) > 0) {
    return [
      "Standard tariff applied (System Settings)",
      "Detailed breakdown was not saved for this quote",
    ];
  }
  return ["No tariff charged", "No tariff information available for this part"];
};

/** Saved per-line tariff breakdown (History) from bom.tariff_details.tariff_info. */
export const findSavedTariffInfo = (tariffDetails, row, quoteQty) => {
  let details = tariffDetails;
  if (typeof details === "string") {
    try {
      details = JSON.parse(details);
    } catch (e) {
      return null;
    }
  }
  const byPart = details?.tariff_info;
  if (!byPart || typeof byPart !== "object" || quoteQty == null) {
    return null;
  }
  const candidates = [
    row?.obsolete_part_number,
    row?.manufacturer_part_number,
    row?.selected_alternate_mpn,
  ];
  for (const mpn of candidates) {
    const key = String(mpn || "").trim().toUpperCase();
    const info = key ? byPart[key]?.[String(quoteQty).trim()] : null;
    if (info) {
      return info;
    }
  }
  return null;
};

const TariffInfoIcon = ({ info, amountText, quoteStatus }) => {
  const [id] = useState(() => `tariff-info-${++tariffInfoSeq}`);
  const [open, setOpen] = useState(false);
  const lines = tariffInfoLines({ info, amountText, quoteStatus });

  return (
    <>
      <span
        id={id}
        className="tariff-info-icon"
        aria-label="Tariff details"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        i
      </span>
      <Tooltip
        target={id}
        isOpen={open}
        trigger="manual"
        fade={false}
        placement="top"
        popperClassName="tariff-info-popper"
        innerClassName="tariff-info-tooltip"
      >
        <ul>
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      </Tooltip>
    </>
  );
};

export default TariffInfoIcon;
